import {
  Banner,
  Box,
  Breadcrumb,
  Grid,
  GridColumn,
  Tabs,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { AutoPayModal } from "@/components/Payments/AutoPayModal/AutoPayModal";
import { SummaryBanner, SummaryBannerProps } from "@/components/SummaryBanner";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { useInvoiceService } from "@/hooks/useInvoiceService";
import { AutoPayTypes } from "@/types/PaymentMethod";
import { attachCurrencySymbolToBalanceString } from "@/utils/InvoicePay";

import InvoiceList from "../components/InvoicePay/InvoiceList";

const pages = [
  { pageId: "your-account", title: "Your Account" },
  {
    pageId: "your-account/invoices-and-payments",
    title: "Invoices and payments"
  }
];

const InvoicesAndPaymentsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const creditsEnabled = useFeatureFlag("SEE_AND_APPLY_CREDITS");
  const autoPayAndSchedulePayEnabled = useFeatureFlag(
    "IS_AUTO_PAY_SCHEDULE_PAY_ENABLED"
  );

  const [showAutoPayModal, setShowAutoPayModal] = React.useState(false);

  const { isLoadingSummary, showSummaryError, summaryData } =
    useInvoiceService();

  const tabs = React.useMemo(() => {
    const availableTabs = [{ id: "invoices", label: "Invoices" }];
    if (creditsEnabled) availableTabs.push({ id: "credits", label: "Credits" });
    return availableTabs;
  }, [creditsEnabled]);

  const [activeTab, setActiveTab] = React.useState<string>(() => {
    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    const validTab = tabs.find((t) => t.id === tabFromUrl);

    if (validTab) return validTab.id;
    return tabs[0]?.id || "invoices";
  });

  const onClickTab = (tabId: string) => {
    setActiveTab(tabId);

    const params = new URLSearchParams(location.search);
    params.set("tab", tabId);

    history.push({
      pathname: location.pathname,
      search: params.toString()
    });
  };

  const invoiceBannerActions: SummaryBannerProps["actions"] = [
    {
      label: "Pay balance",
      onClick: () => {},
      icon: "arrow-right",
      variant: "filled"
    },
    {
      label: "Set up automatic payments",
      onClick: () => {
        setShowAutoPayModal(true);
      },
      variant: "outline"
    }
  ];

  const onClickBreadcrumb = (pageId: string) => {
    history.push(`/${pageId}`);
  };

  const pageContent = React.useMemo(() => {
    switch (activeTab) {
      case "invoices":
        return <InvoicesView />;
      case "credits":
        return <CreditsView />;
      case "statements":
        return <StatementsView />;
      default:
        return <InvoicesView />;
    }
  }, [activeTab]);

  return (
    <ThemeProvider>
      {autoPayAndSchedulePayEnabled && (
        <AutoPayModal
          modalType={AutoPayTypes.AllInvoices}
          isVisible={showAutoPayModal}
          setVisible={setShowAutoPayModal}
        />
      )}

      <Box
        sx={{ backgroundColor: "surface-neutral-secondary", py: "spacing-9" }}
      >
        <Grid>
          <GridColumn sx={{ pb: "spacing-9" }}>
            <Breadcrumb pages={pages} onClickPage={onClickBreadcrumb} />
          </GridColumn>
          <GridColumn sx={{ pt: "spacing-6", pb: "spacing-9" }}>
            <Text variant="h4">Invoices and payments</Text>
          </GridColumn>

          {!isLoadingSummary && (
            <GridColumn sx={{ pb: "spacing-9" }}>
              <SummaryBanner
                label="Outstanding balance"
                amount={attachCurrencySymbolToBalanceString(
                  summaryData?.total ?? 0,
                  summaryData?.currency ?? "USD"
                )}
                {...(creditsEnabled && {
                  onClickCreditsLink: () => onClickTab("credits")
                })}
                credits={attachCurrencySymbolToBalanceString(
                  summaryData?.credits,
                  summaryData?.currency ?? "USD"
                )}
                {...(autoPayAndSchedulePayEnabled && {
                  actions: invoiceBannerActions
                })}
                showError={showSummaryError}
              />
              {!creditsEnabled && (
                <Box sx={{ mt: "spacing-9" }}>
                  <Banner
                    variant="info"
                    text="Access to view credits is planned for release in early summer as part of our continued site enhancements to ModernaDirect.com."
                  />
                </Box>
              )}
              {creditsEnabled && (
                <Box sx={{ mt: "spacing-9" }}>
                  <Tabs
                    key={activeTab}
                    items={tabs}
                    defaultActive={activeTab}
                    onClickTab={onClickTab}
                  />
                </Box>
              )}
            </GridColumn>
          )}
        </Grid>

        {pageContent}
      </Box>
    </ThemeProvider>
  );
};

const InvoicesView: React.FC = () => {
  const {
    isLoadingInvoices,
    invoiceError,
    invoiceListData,
    pageSize,
    currentPage,
    sortDirection,
    sortProperty,
    handlePageChange,
    handleSort,
    handlePageSizeChange
  } = useInvoiceService();

  return (
    <Grid>
      <GridColumn sx={{ pb: "spacing-10" }}>
        {isLoadingInvoices ? (
          <LoadingSpinner size="xl" />
        ) : invoiceError ? (
          <Text sx={{ pt: "spacing-6", color: "color-system-red-700" }}>
            {invoiceError}
          </Text>
        ) : !invoiceListData?.invoices?.length ? (
          <Text sx={{ pt: "spacing-6" }} color="text-error">
            No invoices found.
          </Text>
        ) : (
          <InvoiceList
            invoiceList={invoiceListData?.invoices || []}
            pageSize={pageSize}
            totalPage={Math.ceil(
              (invoiceListData?.pagination?.total ?? 0) / pageSize
            )}
            currentPage={currentPage}
            sortDirection={sortDirection}
            sortProperty={sortProperty}
            onPageChange={handlePageChange}
            onSort={handleSort}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </GridColumn>
    </Grid>
  );
};

const CreditsView: React.FC = () => {
  return (
    <Grid>
      <GridColumn>
        <Text>Credits Content</Text>
      </GridColumn>
    </Grid>
  );
};

const StatementsView: React.FC = () => {
  return (
    <Grid>
      <GridColumn>
        <Text>Statements Content</Text>
      </GridColumn>
    </Grid>
  );
};

export default InvoicesAndPaymentsPage;
